import { useState, useEffect } from 'react';
import { createAgent } from '../../services/agentService';
import { searchClients } from '../../services/clientService';
import { searchProperties } from '../../services/propertyService';
import type { Client } from '../../types/client';
import type { Property } from '../../types/property';

interface AddAgentModalProps {
  onClose: () => void;
  onSuccess: () => void;
  clientId?: string;
  propertyId?: string;
}

export function AddAgentModal({ onClose, onSuccess, clientId, propertyId }: AddAgentModalProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedClientId, setSelectedClientId] = useState(clientId || '');
  const [selectedPropertyId, setSelectedPropertyId] = useState(propertyId || '');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    searchClients({}, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setClients);
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      setProperties([]);
      return;
    }
    searchProperties({ clientId: selectedClientId }, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setProperties);
  }, [selectedClientId]);

  function handleClientChange(newClientId: string) {
    setSelectedClientId(newClientId);
    if (!propertyId) setSelectedPropertyId('');
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId || !selectedPropertyId || !name.trim()) return;

    setError('');
    setLoading(true);
    try {
      await createAgent({
        clientId: selectedClientId,
        propertyId: selectedPropertyId,
        name: name.trim(),
      });
      onSuccess();
    } catch {
      setError('Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Add Call Agent</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Client *</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  disabled={!!clientId}
                  required
                  className={`${inputClass} ${clientId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select a client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Property *</label>
                <select
                  value={selectedPropertyId}
                  onChange={(e) => setSelectedPropertyId(e.target.value)}
                  disabled={!!propertyId || !selectedClientId}
                  required
                  className={`${inputClass} ${propertyId || !selectedClientId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">{selectedClientId ? 'Select a property' : 'Select a client first'}</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className={inputClass}
                  placeholder="Agent name"
                />
              </div>
            </div>
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedClientId || !selectedPropertyId || !name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Adding...' : 'Add Call Agent'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
